// The admin chrome (host shell) entry point.
//
// `mount(rootEl)` is the ONE thing main.ts calls. It:
//   1. Validates the session against `portal.auth.me`.
//   2. Renders either the login screen (email + password) or the full shell.
//   3. Registers stub pages (and any real pages the siblings have shipped).
//   4. Wires the router into the content area.

import { api } from "@tkos/api-client";
import { getCurrentUser, onAuthChange, login } from "./auth";
import { startRouter, navigate, listPages } from "./router";
import { createHeader } from "./header";
import { createSidebar } from "./nav";
import "./pages-registry"; // side-effect: registers real pages + stub fallbacks
import type { AdminUser, PageContext, ToastKind } from "./types";

// Base palette + primitives must be bundled. The chrome's own layout/header
// styles live in ./styles.css.
import "../style.css";
import "./styles.css";

// ── Toast region ─────────────────────────────────────────────────────────────

interface ToastFn {
  (msg: string, kind?: ToastKind): void;
}

function createToaster(): { node: HTMLElement; show: ToastFn } {
  const node = document.createElement("div");
  node.className = "adm-toaster";
  node.setAttribute("role", "status");
  node.setAttribute("aria-live", "polite");

  const show: ToastFn = (msg, kind = "info") => {
    const t = document.createElement("div");
    t.className = `adm-toast adm-toast--${kind}`;
    t.textContent = msg;
    node.appendChild(t);
    window.setTimeout(() => {
      t.classList.add("is-leaving");
      window.setTimeout(() => t.remove(), 250);
    }, 3500);
  };

  return { node, show };
}

// ── Retailer filter (god-mode chrome state) ──────────────────────────────────
// Pages can drill into a single retailer. The chrome owns this state so the
// header's pill stays in sync regardless of which page set it.

interface RetailerFilterStore {
  get: () => string | null;
  set: (id: string | null) => void;
  getLabel: () => string;
  subscribe: (fn: () => void) => () => void;
}

function createRetailerFilterStore(): RetailerFilterStore {
  let id: string | null = null;
  let label = "";
  const subs = new Set<() => void>();
  return {
    get: () => id,
    set: (next) => {
      if (next === id) return;
      id = next;
      // For now we use the id as both the value and the (fallback) label.
      // A future page can extend the store with `setLabel` if a human-friendly
      // name is required in the pill.
      label = next ?? "";
      for (const fn of subs) fn();
    },
    getLabel: () => label,
    subscribe: (fn) => {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
  };
}

// ── Login screen ─────────────────────────────────────────────────────────────

function renderLogin(rootEl: HTMLElement, onSuccess: () => void): void {
  rootEl.replaceChildren();
  const wrap = document.createElement("div");
  wrap.className = "adm-login";
  wrap.innerHTML = `
    <form class="adm-login__card" autocomplete="on">
      <div class="adm-login__brand">
        <span class="adm-login__badge">TKOS</span>
      </div>
      <div class="adm-login__sub">Admin Portal</div>
      <div class="adm-login__divider"></div>
      <p class="adm-login__lede">Sign in with your admin credentials.</p>
      <input
        class="adm-login__input"
        type="email"
        name="email"
        placeholder="Email"
        autocomplete="username"
        required
      />
      <input
        class="adm-login__input"
        type="password"
        name="password"
        placeholder="Password"
        autocomplete="current-password"
        required
      />
      <button class="adm-login__btn" type="submit">Sign in</button>
      <div class="adm-login__error" data-role="error"></div>
    </form>
  `;
  rootEl.appendChild(wrap);

  const form = wrap.querySelector("form")!;
  const emailInput = wrap.querySelector<HTMLInputElement>("input[name='email']")!;
  const pwInput = wrap.querySelector<HTMLInputElement>("input[name='password']")!;
  const btn = wrap.querySelector<HTMLButtonElement>("button[type='submit']")!;
  const errEl = wrap.querySelector<HTMLDivElement>("[data-role='error']")!;

  // Focus the email shortly after render so it doesn't get clobbered by HMR.
  window.setTimeout(() => emailInput.focus(), 50);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = emailInput.value.trim();
    const pw = pwInput.value;
    if (!pw) return;

    btn.disabled = true;
    btn.textContent = "Signing in…";
    errEl.textContent = "";

    try {
      await login(email, pw);
      onSuccess();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = "Sign in";
      pwInput.value = "";
      pwInput.focus();
      errEl.textContent = err instanceof Error ? err.message : "Login failed.";
    }
  });
}

// ── App shell ────────────────────────────────────────────────────────────────

function renderAppShell(
  rootEl: HTMLElement,
  user: AdminUser,
  onLoggedOut: () => void,
): () => void {
  rootEl.replaceChildren();

  const layout = document.createElement("div");
  layout.className = "adm-layout";

  const closeMobileNav = (): void => {
    layout.classList.remove("is-nav-open");
  };

  // Retailer filter store — shared between header pill + PageContext.
  const filterStore = createRetailerFilterStore();

  const header = createHeader({
    onToggleNav: () => layout.classList.toggle("is-nav-open"),
    onLoggedOut,
    getRetailerFilter: () => {
      const id = filterStore.get();
      if (!id) return null;
      return { id, label: filterStore.getLabel() || id };
    },
    clearRetailerFilter: () => filterStore.set(null),
    onRetailerFilterChange: filterStore.subscribe,
  });

  const sidebar = createSidebar({
    onAfterNavigate: closeMobileNav,
  });

  const main = document.createElement("main");
  main.className = "adm-main";
  main.id = "adm-main";
  main.setAttribute("role", "main");
  main.tabIndex = -1;

  const toaster = createToaster();

  // Backdrop for the mobile sidebar drawer.
  const backdrop = document.createElement("div");
  backdrop.className = "adm-backdrop";
  backdrop.addEventListener("click", closeMobileNav);

  layout.append(header.el, sidebar, main, backdrop, toaster.node);
  rootEl.appendChild(layout);

  const ctx: PageContext = {
    api,
    user,
    navigate: (name) => navigate(name),
    showToast: toaster.show,
    getCurrentRetailerId: () => filterStore.get(),
    setCurrentRetailerId: (id) => filterStore.set(id),
  };

  // Default page: dashboard.
  const defaultPage =
    listPages().find((p) => p.name === "dashboard")?.name ??
    listPages()[0]?.name ??
    "dashboard";

  const stop = startRouter({ container: main, ctx, defaultPage });

  // Keep PageContext.user in sync with auth changes for future mounts.
  const offAuth = onAuthChange((next) => {
    if (next) ctx.user = next;
  });

  return () => {
    stop();
    offAuth();
    header.destroy();
  };
}

// ── Public entry ─────────────────────────────────────────────────────────────

let teardownShell: (() => void) | null = null;

/**
 * Mount the chrome into `rootEl`. Idempotent — calling again tears down the
 * previous mount (handy for HMR safety in development).
 */
export async function mount(rootEl: HTMLElement): Promise<void> {
  if (teardownShell) {
    teardownShell();
    teardownShell = null;
  }

  // Initial loading flash so we don't show a blank white screen while
  // `auth.me` is in flight.
  rootEl.replaceChildren();
  const loading = document.createElement("div");
  loading.className = "adm-bootstrap";
  loading.textContent = "Loading…";
  rootEl.appendChild(loading);

  const renderForCurrentAuth = async (): Promise<void> => {
    const user = await getCurrentUser();
    if (user) {
      teardownShell = renderAppShell(rootEl, user, () => {
        // After logout: re-check auth (now null) and render login.
        void renderForCurrentAuth();
      });
    } else {
      if (teardownShell) {
        teardownShell();
        teardownShell = null;
      }
      renderLogin(rootEl, () => {
        // After successful login: re-render the shell with auth populated.
        void renderForCurrentAuth();
      });
    }
  };

  await renderForCurrentAuth();
}
